import { useState, useMemo } from "react";
import { BarChart3, BookOpen, Search, X } from "lucide-react";
import { PageHeader } from "~/components/PageHeader";
import type { Measure } from "~/modules/measures/types";
import type { ReferenceData } from "~/modules/reference_data/types";
import { CatalogOverviewSidebar } from "~/modules/catalog/components/CatalogOverviewSidebar";
import { EnterpriseCTA } from "~/modules/billing/components/EnterpriseCTA";
import { MeasureItem } from "~/modules/catalog/pages/measure-list/measures-list";
import { ReferenceDataItem } from "~/modules/catalog/pages/reference-list/reference-data-list";
import { ScrollArea } from "~/components/ui/scroll-area";
import { getMeasureURL, getReferenceDataURL } from "~/routes";
import { FeaturedCard } from "./FeaturedCard";

// // // //

const FEATURED_LIMIT = 4;

// // // //

interface Props {
    measures: Measure[];
    referenceData: ReferenceData[];
    totalAssetCount: number;
}

// // // //

interface CategoryGroup<T> {
    category: string;
    order: number;
    items: T[];
}

function groupByCategory<
    T extends {
        category: string | null;
        category_order: number;
        item_order: number;
    },
>(items: T[]): CategoryGroup<T>[] {
    const groups = new Map<string, { order: number; items: T[] }>();
    for (const item of items) {
        if (!item.category) continue;
        const existing = groups.get(item.category);
        if (existing) {
            existing.items.push(item);
        } else {
            groups.set(item.category, {
                order: item.category_order,
                items: [item],
            });
        }
    }
    return Array.from(groups, ([category, { order, items }]) => ({
        category,
        order,
        items: items.sort((a, b) => a.item_order - b.item_order),
    })).sort((a, b) => a.order - b.order);
}

// // // //

export function DataCatalogOverview(props: Props) {
    const { measures, referenceData, totalAssetCount } = props;
    const [searchQuery, setSearchQuery] = useState("");
    const isSearching = searchQuery.trim().length > 0;

    const sortedMeasures = useMemo(
        () => [...measures].sort((a, b) => a.name.localeCompare(b.name)),
        [measures],
    );

    const sortedReferenceData = useMemo(
        () => [...referenceData].sort((a, b) => a.name.localeCompare(b.name)),
        [referenceData],
    );

    // Featured items (limited to FEATURED_LIMIT each)
    const featuredMeasures = useMemo(
        () =>
            measures
                .filter((m) => m.featured)
                .sort((a, b) => a.item_order - b.item_order)
                .slice(0, FEATURED_LIMIT),
        [measures],
    );

    const featuredReferenceData = useMemo(
        () =>
            referenceData
                .filter((item) => item.featured)
                .sort((a, b) => a.item_order - b.item_order)
                .slice(0, FEATURED_LIMIT),
        [referenceData],
    );

    // Filtered + grouped for complete list
    const filteredMeasures = useMemo(() => {
        if (!isSearching) return measures;
        const q = searchQuery.toLowerCase();
        return measures.filter(
            (m) =>
                m.display_name.toLowerCase().includes(q) ||
                m.name.toLowerCase().includes(q) ||
                m.slug.toLowerCase().includes(q) ||
                (m.category && m.category.toLowerCase().includes(q)),
        );
    }, [measures, searchQuery, isSearching]);

    const filteredReferenceData = useMemo(() => {
        if (!isSearching) return referenceData;
        const q = searchQuery.toLowerCase();
        return referenceData.filter(
            (item) =>
                item.name.toLowerCase().includes(q) ||
                item.slug.toLowerCase().includes(q) ||
                item.category.toLowerCase().includes(q),
        );
    }, [referenceData, searchQuery, isSearching]);

    const measureGroups = useMemo(
        () => groupByCategory(filteredMeasures),
        [filteredMeasures],
    );

    const referenceDataGroups = useMemo(
        () => groupByCategory(filteredReferenceData),
        [filteredReferenceData],
    );

    const hasResults =
        filteredMeasures.length > 0 || filteredReferenceData.length > 0;

    return (
        <div className="flex h-full min-h-0 overflow-hidden">
            <CatalogOverviewSidebar
                measures={sortedMeasures}
                referenceData={sortedReferenceData}
            />

            <ScrollArea className="flex-1 min-h-0 min-w-0 overflow-hidden">
                <div className="flex flex-col pt-2 pb-8 px-6">
                    <PageHeader
                        title="Data Catalog"
                        subtitle={`Explore our comprehensive data offerings for tokenized assets — ${totalAssetCount.toLocaleString()} assets across metrics and reference data.`}
                    />

                    <EnterpriseCTA showDocsLink slim />

                    {/* Featured Metrics */}
                    {featuredMeasures.length > 0 && (
                        <div className="mt-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {featuredMeasures.map((measure) => (
                                    <FeaturedCard
                                        key={measure.id}
                                        label={measure.display_name}
                                        slug={measure.slug}
                                        description={measure.short_description}
                                        href={getMeasureURL({
                                            measureSlug: measure.slug,
                                        })}
                                        icon={measure.icon}
                                        color={measure.color}
                                        assetCount={measure.asset_count}
                                        totalAssetCount={totalAssetCount}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Featured Reference Data */}
                    {featuredReferenceData.length > 0 && (
                        <div className="mt-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {featuredReferenceData.map((item) => (
                                    <FeaturedCard
                                        key={item.id}
                                        label={item.name}
                                        slug={item.slug}
                                        description={item.short_description}
                                        href={getReferenceDataURL({
                                            referenceSlug: item.slug,
                                        })}
                                        icon={item.icon}
                                        color={item.color}
                                        assetCount={item.asset_count}
                                        totalAssetCount={totalAssetCount}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Search bar */}
                    <div className="mt-6 mb-6">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <input
                                type="search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search all metrics and reference data..."
                                className="w-full rounded-lg border border-border bg-card py-3 pl-12 pr-10 text-base placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            {isSearching && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Complete list */}
                    {hasResults ? (
                        <div className="space-y-8">
                            {/* All Metrics */}
                            {measureGroups.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        <BarChart3 className="h-5 w-5 text-muted-foreground" />
                                        <h2 className="text-base font-semibold text-foreground select-none">
                                            Metrics
                                        </h2>
                                        <span className="text-xs text-muted-foreground">
                                            {filteredMeasures.length}
                                        </span>
                                    </div>
                                    <div className="space-y-6">
                                        {measureGroups.map((group) => (
                                            <div key={group.category}>
                                                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 pl-1">
                                                    {group.category}
                                                </h3>
                                                <div className="grid grid-cols-1 gap-3">
                                                    {group.items.map(
                                                        (measure) => (
                                                            <MeasureItem
                                                                key={measure.id}
                                                                measure={
                                                                    measure
                                                                }
                                                                totalAssetCount={
                                                                    totalAssetCount
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* All Reference Data */}
                            {referenceDataGroups.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        <BookOpen className="h-5 w-5 text-muted-foreground" />
                                        <h2 className="text-base font-semibold text-foreground select-none">
                                            Reference Data
                                        </h2>
                                        <span className="text-xs text-muted-foreground">
                                            {filteredReferenceData.length}
                                        </span>
                                    </div>
                                    <div className="space-y-6">
                                        {referenceDataGroups.map((group) => (
                                            <div key={group.category}>
                                                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 pl-1">
                                                    {group.category}
                                                </h3>
                                                <div className="grid grid-cols-1 gap-3">
                                                    {group.items.map((item) => (
                                                        <ReferenceDataItem
                                                            key={item.id}
                                                            item={item}
                                                            totalAssetCount={
                                                                totalAssetCount
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-muted mb-3">
                                <Search className="h-7 w-7 text-muted-foreground" />
                            </div>
                            <h3 className="text-base font-semibold text-foreground mb-1">
                                No results found
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">
                                No items match &ldquo;{searchQuery}&rdquo;
                            </p>
                            <button
                                onClick={() => setSearchQuery("")}
                                className="text-sm text-primary hover:underline"
                            >
                                Clear search
                            </button>
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}
